import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Link, Navigate } from "react-router-dom";
import StatusPill from "../../components/StatusPill";
import { fmtDT } from "../../lib/constants";
import { toast } from "sonner";
import { QrCode, Camera, CheckCircle } from "@phosphor-icons/react";
import CameraEvidenceCapture from "../../components/evidence/CameraEvidenceCapture";
import { HandledHistoryPanel } from "../../components/HandledHistoryPanel";
import { CargoAlert } from "../../components/CargoAlert";

export default function CaptainHome() {
  const { profile } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [active, setActive] = useState(null);
  const [completeness, setCompleteness] = useState({});
  const [scanContext, setScanContext] = useState("SOURCE_RECEIPT");
  const [manualToken, setManualToken] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [destVehicles, setDestVehicles] = useState([]);
  const [cargo, setCargo] = useState(null);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  const refresh = () => {
    api.get("/shipments").then((r) => setShipments(r.data)).catch((e) => toast.error(e.displayMessage));
  };

  const refreshCompleteness = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/shipments/${active.id}/scans/completeness`, { params: { context: scanContext } });
      setCompleteness(data);
    } catch (e) {}
    try {
      const r = await api.get(`/shipments/${active.id}/destination-vehicle`);
      setDestVehicles(r.data?.vehicles || []);
    } catch (e) { setDestVehicles([]); }
  }, [active, scanContext]);

  useEffect(() => {
    refresh();
    // Refetch on window focus so newly assigned shipments appear without manual reload
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Default scan context per captain side (airport binding vs the active shipment)
  useEffect(() => {
    if (!profile || profile.role !== "CAP" || !active) return;
    if (profile.airport_code === active.source_airport) setScanContext("SOURCE_RECEIPT");
    else if (profile.airport_code === active.destination_airport) setScanContext("DESTINATION_RECEIPT");
  }, [profile, active]);

  useEffect(() => {
    refreshCompleteness();
  }, [refreshCompleteness]);

  useEffect(() => {
    if (!active?.id) { setCargo(null); return; }
    api.get(`/shipments/${active.id}`)
      .then((r) => setCargo({
        restrictedGoods: r.data?.shipment?.restricted_goods_declared || [],
        fragilePackages: (r.data?.packages || []).filter((p) => p.fragile).map((p) => p.public_label),
      }))
      .catch(() => setCargo(null));
  }, [active]);

  if (!profile) return null;
  if (!["CAP","SUPER_ADMIN"].includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  const startScanner = async () => {
    const { Html5Qrcode } = await import("html5-qrcode");
    if (!scannerRef.current) return;
    const config = { fps: 8, qrbox: 240 };
    const q = new Html5Qrcode(scannerRef.current.id);
    html5QrRef.current = q;
    try {
      await q.start({ facingMode: "environment" }, config, onScan, () => {});
    } catch (e) {
      toast.error(`Camera failed: ${e.message || e}`);
    }
  };
  const stopScanner = async () => {
    try { await html5QrRef.current?.stop(); } catch {}
    try { await html5QrRef.current?.clear(); } catch {}
    html5QrRef.current = null;
  };

  const onScan = async (text) => {
    // text may be a full URL `${FRONTEND}/scan/pkg/{token}`; extract token
    let token = text;
    const idx = text.lastIndexOf("/");
    if (idx >= 0) token = text.slice(idx + 1);
    await submitScan(token);
  };

  const submitScan = async (token) => {
    if (!active) return toast.error("Pick a shipment first");
    try {
      const { data } = await api.post(`/shipments/${active.id}/scan`, { scan_token: token, scan_context: scanContext });
      toast.success(`✓ ${data.package_label} (${data.scanned_count}/${data.expected_count})`);
      setCompleteness((c) => ({ ...c, scanned_count: data.scanned_count, expected_count: data.expected_count, complete: data.complete }));
      // Destination receipt: surface the scanned package for its receipt photo.
      if (scanContext === "DESTINATION_RECEIPT" && data.package_id) {
        setScanResult(data);
      }
      refreshCompleteness();
    } catch (e) {
      toast.error(e.displayMessage);
    }
  };

  const capSide = profile.role === "CAP" && active
    ? (profile.airport_code === active.source_airport ? "SOURCE"
       : profile.airport_code === active.destination_airport ? "DESTINATION" : null)
    : null;
  // Physical package scanning is the assigned Captain's custody act only; Super Admin sees read-only progress.
  const canScan = profile.role === "CAP";
  // Destination physical-checkpoint evidence (DESTINATION_RECEIPT_PHOTO / VEHICLE_LOADED) is Destination-Captain
  // only (backend DEST_CAP_ONLY_TYPES); Super Admin sees the read-only capture status.
  const isDestCap = profile.role === "CAP" && capSide === "DESTINATION";
  // AIR12 FD-2 — VEHICLE_LOADED capture/replacement only in the destination vehicle-edit window (backend
  // evidence_router.VEHICLE_PHOTO_WINDOWS); outside it the row + captured indicator stay read-only.
  const vehiclePhotoWindowOpen = ["RECEIVER_READY_CONFIRMED", "DESTINATION_VEHICLE_ASSIGNED"].includes(active?.current_status);

  return (
    <div className="max-w-[700px] mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">CAPTAIN · {profile.role.replace(/_/g, " ")}</div>
          <h1 className="heading text-2xl">Assigned Shipments</h1>
        </div>
        <button className="btn-ghost text-xs" onClick={refresh} data-testid="captain-refresh">Refresh</button>
      </div>

      {!active ? (
        <div className="space-y-2">
          {shipments.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted))]" data-testid="no-captain-shipments">No assigned shipments yet.</p>
          ) : shipments.map((s) => (
            <button key={s.id} onClick={() => setActive(s)} className="grid-card grid-card-strong w-full text-left p-4 hover:bg-[hsl(var(--surface-hover))]" data-testid={`captain-shipment-${s.public_shipment_number}`}>
              <div className="flex items-center justify-between">
                <span className="id-chip id-chip-strong">{s.public_shipment_number}</span>
                <StatusPill status={s.current_status} />
              </div>
              <div className="mt-2 text-xs text-[hsl(var(--muted))]">
                {s.source_airport} → {s.destination_airport} · {s.total_chargeable_weight_kg} kg · {fmtDT(s.created_at)}
              </div>
            </button>
          ))}
          <div className="mt-8">
            <HandledHistoryPanel testidPrefix="captain-handled-history" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <button onClick={() => { stopScanner(); setActive(null); }} className="text-xs underline" data-testid="back-to-list">← Back to list</button>
          <div className="grid-card grid-card-strong p-4">
            <span className="id-chip id-chip-strong">{active.public_shipment_number}</span>
            <div className="mt-2 text-xs text-[hsl(var(--muted))]">{active.source_airport} → {active.destination_airport}</div>
            <div className="mt-2"><StatusPill status={active.current_status} /></div>
            {/* Captain duties on Shipment Detail (custody receipt, AWB, airline handover, Out for Delivery). */}
            <Link to={`/admin/shipments/${active.id}`} className="btn-secondary text-xs mt-3 inline-flex" data-testid="captain-open-shipment-detail">
              Open shipment
            </Link>
          </div>

          {cargo && (
            <CargoAlert
              restrictedGoods={cargo.restrictedGoods}
              fragilePackages={cargo.fragilePackages}
              testid="captain-cargo-alert"
            />
          )}

          <div className="grid-card">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="overline">{canScan ? "SCAN PACKAGES" : "SCAN PROGRESS"}</div>
              <select className="input-field text-xs h-8 w-auto" value={scanContext} onChange={(e) => setScanContext(e.target.value)} data-testid="scan-context-select">
                {capSide === "SOURCE" ? (
                  <option value="SOURCE_RECEIPT">SOURCE RECEIPT</option>
                ) : capSide === "DESTINATION" ? (
                  <option value="DESTINATION_RECEIPT">DESTINATION RECEIPT</option>
                ) : (
                  <>
                    <option value="SOURCE_RECEIPT">SOURCE RECEIPT</option>
                    <option value="DESTINATION_RECEIPT">DESTINATION RECEIPT</option>
                  </>
                )}
              </select>
            </div>
            <div className="p-4">
              {canScan && (
                <>
                  <div id="captain-qr" ref={scannerRef} className="w-full max-w-md aspect-square border border-[hsl(var(--border-strong))] rounded-sm overflow-hidden mb-3" data-testid="qr-scanner-region" />
                  <div className="flex gap-2">
                    <button className="btn-primary" onClick={startScanner} data-testid="start-scanner-button"><Camera size={14} /> Start camera</button>
                    <button className="btn-ghost" onClick={stopScanner} data-testid="stop-scanner-button">Stop</button>
                  </div>

                  <div className="mt-4">
                    <div className="overline mb-1.5">OR ENTER TOKEN MANUALLY</div>
                    <div className="flex gap-2">
                      <input className="input-field" placeholder="Paste QR token" value={manualToken} onChange={(e) => setManualToken(e.target.value)} data-testid="manual-token-input" />
                      <button className="btn-primary" onClick={() => { submitScan(manualToken); setManualToken(""); }} data-testid="submit-manual-token">Submit</button>
                    </div>
                  </div>
                </>
              )}

              <div className={`${canScan ? "mt-6 " : ""}grid-card p-4`}>
                <div className="overline">COMPLETENESS · {scanContext.replace(/_/g, " ")}</div>
                <div className={`heading text-3xl mono mt-2 ${completeness.complete ? "text-[hsl(var(--success))]" : ""}`} data-testid="completeness-counter">
                  {completeness.scanned_count ?? 0}/{completeness.expected_count ?? 0}
                </div>
                {completeness.complete && (
                  <div className="mt-2 text-sm text-[hsl(var(--success))] flex items-center gap-1.5">
                    <CheckCircle size={14} weight="fill" /> All packages scanned
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid-card" data-testid="captain-evidence-photos">
            <div className="px-4 py-3 border-b"><div className="overline">EVIDENCE PHOTOS</div></div>
            <div className="p-4 space-y-3">
              {(profile.role === "CAP" && capSide !== "DESTINATION") || profile.role === "SUPER_ADMIN" ? (
                <CameraEvidenceCapture
                  shipmentId={active.id}
                  docType="SAL_SOURCE_RECEIPT"
                  label="Authority Letter at source airport"
                  stage="SOURCE_RECEIPT"
                  onCaptured={refreshCompleteness}
                  buttonClassName="w-full border border-dashed border-[hsl(var(--border))] rounded px-4 py-3 text-sm inline-flex items-center justify-center gap-2"
                  buttonLabel="Take photo — Authority Letter at source airport (SAL)"
                />
              ) : null}
              {isDestCap || profile.role === "SUPER_ADMIN" ? (
                <>
                  {isDestCap && scanContext === "DESTINATION_RECEIPT" && scanResult?.package_id ? (
                    <CameraEvidenceCapture
                      shipmentId={active.id}
                      docType="DESTINATION_RECEIPT_PHOTO"
                      packageId={scanResult.package_id}
                      label={`Receipt photo for ${scanResult.package_label || "scanned package"}`}
                      stage="DESTINATION_RECEIPT"
                      onCaptured={() => { refreshCompleteness(); setScanResult(null); }}
                      buttonClassName="w-full border border-dashed border-[hsl(var(--border))] rounded px-4 py-3 text-sm inline-flex items-center justify-center gap-2"
                      buttonLabel={`Take receipt photo — ${scanResult.package_label || "package"}`}
                    />
                  ) : null}
                  <div className="space-y-3" data-testid="captain-loaded-vehicles">
                    {destVehicles.map((v) => (
                      <div key={v.id} className="flex items-center gap-3" data-testid={`captain-loaded-photo-row-${v.id}`}>
                        <span className="text-sm flex-1">
                          {v.vehicle_number || "—"}
                          {v.photo_covered && <span className="ml-2 text-[hsl(var(--success))] text-xs">✓ captured</span>}
                        </span>
                        {isDestCap && vehiclePhotoWindowOpen && <CameraEvidenceCapture
                          shipmentId={active.id}
                          docType="VEHICLE_LOADED"
                          roadLegId={v.id}
                          label={`Boxes loaded (VEHICLE_LOADED) · ${v.vehicle_number}`}
                          stage="DESTINATION"
                          onCaptured={refreshCompleteness}
                          buttonClassName="border border-dashed border-[hsl(var(--border))] rounded px-3 py-2 text-xs inline-flex items-center gap-2"
                          buttonLabel="Take VEHICLE_LOADED photo"
                        />}
                      </div>
                    ))}
                    {isDestCap && vehiclePhotoWindowOpen && destVehicles.length === 0 && (
                      <div className="text-[11px] text-amber-600">VEHICLE_LOADED photos can be captured once destination vehicles are assigned.</div>
                    )}
                    {isDestCap && !vehiclePhotoWindowOpen && (
                      <div className="text-[11px] text-[hsl(var(--muted))]" data-testid="captain-vehicle-photo-window-closed">
                        VEHICLE_LOADED photos can be captured only while destination vehicles are editable (Receiver Ready until Out for Delivery).
                      </div>
                    )}
                  </div>
                </>
              ) : null}
              <p className="text-[11px] text-[hsl(var(--muted))]" data-testid="captain-evidence-guidance">
                {!canScan
                  ? "Read-only scan progress. Package scanning is performed by the assigned Source / Destination Captain."
                  : capSide === "DESTINATION"
                  ? "Scan each package (DESTINATION_RECEIPT), then take its receipt photo. Receipt reconciles automatically once every package has both a scan and a photo. Capture VEHICLE_LOADED once the current vehicle is loaded."
                  : "Capture the Authority Letter photo first, then scan each package (SOURCE_RECEIPT). Receipt reconciles automatically on the final scan."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
